import type { RealtimeEnvelope } from '@/shared/realtime/bus';

/**
 * 帧名闭集 —— **后端 `backend/frames/catalog.yaml` 是唯一真源**。
 *
 * 帧名是不可变契约（ADR 0006 §0）：前端认的每个帧名，要么在目录的 `frames`
 * 里，要么在 `exemptions` 豁免表里（豁免表用 `frontend: keep/remove` 写明
 * 前端怎么处理——`keep` = 留着等承载体，`remove` = 从前端一并清干净）。
 *
 * ⚠️ `stream.reconnect` 是**控制帧**，不在目录里（控制帧没有收件人、
 * 不落行、不进发布层 ⇒ 不是业务帧）——会话被撤销 / 用户被禁用时，
 *    服务端先写这个控制帧再收流（ADR 0007 D2）⇒ 前端据此知道
 *    「不是网络抖动，是会话没了」⇒ 停止重连并清会话。
 *    词表在 `features/events/controlFrames.ts`。
 */
export const EVENT_FRAMES = [
  'stream.reconnect',
  'notification.created',
  'notification.broadcast',
  'order.account.granted',
  'order.account.revoked',
  'order.account.deleted',
  'cdkey.bound',
  'redeem.card_processed',
  'redeem.attribute',
  'redeem.expired',
  'proxy.check_speed_done',
  'fx.refresh_failed',
  'fx.refreshed',
  'account.online',
  'account.need_code',
  'account.failed',
] as const;
export type EventFrame = (typeof EVENT_FRAMES)[number];
const FRAME_SET: ReadonlySet<string> = new Set(EVENT_FRAMES);
export function isKnownFrame(frame: string): frame is EventFrame {
  return FRAME_SET.has(frame);
}
/**
 * 解析一条 SSE 消息 ⇒ 帧信封。
 *
 * ⚠️ 信封的类型住在 `shared/realtime/bus.ts`（`RealtimeEnvelope`）——
 * **一处定义**：总线、解析器、消费者都认它。
 */
export function parseEnvelope(raw: unknown, eventType?: string): RealtimeEnvelope | null {
  if (typeof raw !== 'string') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
  const e = parsed as Record<string, unknown>;
  const inData = typeof e.type === 'string' && e.type !== '' ? e.type : null;
  const inEventLine =
    typeof eventType === 'string' && eventType !== '' && eventType !== 'message' ? eventType : null;
  const type = inData ?? inEventLine;
  if (type === null) return null;
  if (inData === null) e.type = type;
  return e as RealtimeEnvelope;
}
